import { createFileRoute, Link } from "@tanstack/react-router";
import { Icon } from "@/components/Icon";

import { Button } from "@/components/ui/button";
import { AGENCY } from "@/lib/agency";
import { buildHead, orgRef } from "@/lib/seo";
import { absoluteUrl } from "@/lib/site-config";
import {
  dallasHub,
  dallasMoneyPillars,
  dallasIndustryPillars,
} from "@/lib/dallas-content";
import { serviceAreas } from "@/lib/service-areas";

export const Route = createFileRoute("/dallas/")({
  head: () =>
    buildHead({
      title: `${dallasHub.h1} | ${AGENCY.name}`,
      description:
        "Compare every business loan program available to Dallas, TX small businesses - SBA loans, working capital, lines of credit, equipment financing, invoice factoring, and industry-specific funding.",
      path: "/dallas/",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Dallas", path: "/dallas/" },
      ],
      graph: [
        {
          "@type": "CollectionPage",
          name: dallasHub.h1,
          url: absoluteUrl("/dallas/"),
          about: "Business loans in Dallas, TX",
          provider: orgRef,
        },
      ],
    }),
  component: DallasHub,
});

const PRODUCT_ICON_NAME: Record<string, string> = {
  "sba-loans": "award",
  "sba-7a-loan": "badgecheck",
  "sba-loan-for-daycare": "heart",
  "sba-loan-for-franchise": "store",
  "sba-express-loan-for-veterans": "shieldcheck",
  "working-capital-loans": "wallet",
  "business-line-of-credit": "creditcard",
  "small-business-loans": "briefcase",
  "short-term-business-loans": "zap",
  "merchant-cash-advance": "receipt",
  "unsecured-business-loans": "sparkles",
  "startup-business-loans": "lightbulb",
  "business-acquisition-loans": "handcoins",
  "accounts-receivable-financing": "filetext",
  "invoice-factoring": "receipt",
  "revenue-based-financing": "trendingup",
  "same-day-business-loans": "clock",
  "business-lenders-near-me": "mappin",
  "business-loan-broker": "compass",
  "commercial-loan-broker": "building2",
  "equipment-financing": "wrench",
  "manufacturing-equipment-financing": "factory",
  "agriculture-equipment-financing": "tractor",
  "landscaping-equipment-financing": "scissors",
  "dental-practice-loans": "stethoscope",
  "medical-practice-loans": "stethoscope",
  "restaurant-loans": "utensils",
  "trucking-business-loans": "truck",
  "commercial-construction-loan": "hammer",
  "hotel-loans": "hotel",
  "salon-business-loans": "scissors",
  "retail-business-loans": "store",
  "auto-repair-shop-financing": "car",
  "ecommerce-business-funding": "shoppingbag",
  "food-truck-financing": "coffee",
  "loan-for-gym-business": "dumbbell",
  "farm-credit-financing": "sprout",
  "women-owned-business-funding": "users",
};

function DallasHub() {
  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            backgroundImage:
              "radial-gradient(ellipse at 85% 0%, oklch(0.42 0.16 250) 0%, transparent 55%), radial-gradient(ellipse at 0% 100%, oklch(0.3 0.1 260) 0%, transparent 50%)",
          }}
        />
        <div className="relative mx-auto max-w-7xl px-6 py-20 md:py-24 lg:px-10">
          <p className="inline-flex items-center gap-2 rounded-full border border-primary-foreground/20 bg-primary-foreground/5 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-primary-foreground/85">
            <Icon name="mappin" color="bl" className="h-3.5 w-3.5 text-accent brightness-0 invert"  />
            Dallas, TX
          </p>
          <h1 className="mt-6 max-w-3xl font-serif text-5xl font-semibold leading-[1.05] tracking-tight md:text-6xl">
            {dallasHub.h1}
          </h1>
          <p className="mt-6 max-w-2xl text-lg leading-relaxed text-primary-foreground/85">
            Dallas businesses run on more than oil and finance. From design
            district studios and Bishop Arts boutiques to Stemmons Corridor
            warehouses and Mockingbird Station clinics, owners across the
            metro need capital that arrives faster than a traditional bank can
            move. {AGENCY.name} matches Dallas operators with{" "}
            {dallasMoneyPillars.length} core financing programs and{" "}
            {dallasIndustryPillars.length} industry-specific lenders, all
            arranged by one local advisor with no upfront fees.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button
              asChild
              size="lg"
              className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90"
            >
              <a href={AGENCY.phoneHref}>
                <Icon name="phonecall" color="bl" className="h-4 w-4 brightness-0 invert"  /> Talk to a Dallas advisor
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="rounded-full border-2 border-white/40 bg-transparent text-white hover:border-white/70 hover:bg-white/10 hover:text-white"
            >
              <Link to="/apply-funding">Apply Now</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="rounded-full border-primary-foreground/30 bg-transparent text-primary-foreground hover:bg-primary-foreground/10"
            >
              <a href="#programs">Browse all programs</a>
            </Button>
          </div>
          <ul className="mt-8 grid max-w-2xl gap-2 text-sm text-primary-foreground/90 sm:grid-cols-3">
            {[
              { icon: "clock", t: "Decisions in 1 hour" },
              { icon: "handcoins", t: "$0 upfront fees" },
              { icon: "shieldcheck", t: "Texas-based advisors" },
            ].map(({ icon: __iconName, t }) => (
              <li key={t} className="flex items-center gap-2">
                <Icon name={__iconName} color="co" className="h-4 w-4 text-accent" />
                {t}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Local intro */}
      <section className="border-b border-border bg-background">
        <div className="mx-auto max-w-4xl px-6 py-16 lg:px-10">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
            Dallas lending landscape
          </p>
          <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight md:text-4xl">
            Why Dallas owners look beyond their bank
          </h2>
          <div className="mt-6 space-y-4 text-base leading-relaxed text-muted-foreground">
            <p>
              Dallas County alone has more than 65,000 employer businesses, and
              the broader DFW metroplex is the fourth largest economy in the
              country. That scale draws every national bank to the city, but
              it also creates a backlog. A typical Dallas SBA file at a
              national bank can sit for 45 to 90 days before underwriting. For
              an HVAC contractor in Garland waiting on a fleet expansion, or a
              clinic in Uptown trying to open a second location before year
              end, that pace is the difference between growth and a missed
              window.
            </p>
            <p>
              We arrange Dallas business loans through a network of SBA
              preferred lenders, regional banks, asset-based lenders, equipment
              specialists, factoring houses, and revenue-based funders. Some
              programs sit in front of Google's map pack for Dallas searches;
              others compete in organic results. Either way, every page on
              this site reflects what the lender will actually approve for a
              Dallas-based, Texas-based business, not generic copy lifted
              from a national site.
            </p>
          </div>
        </div>
      </section>

      {/* Money pillars */}
      <section id="programs" className="border-b border-border bg-secondary/40">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
                Core programs
              </p>
              <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight md:text-4xl">
                Business loan programs in Dallas, TX
              </h2>
              <p className="mt-4 text-muted-foreground">
                {dallasMoneyPillars.length} financing structures used by Dallas
                small and mid-sized businesses. Pick the page that matches
                your need.
              </p>
            </div>
          </div>

          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {dallasMoneyPillars.map((p) => {
              const iconName = PRODUCT_ICON_NAME[p.slug!] ?? "dollarsign";
              return (
              <Link
                key={p.slug!}
                to="/dallas/$slug"
                params={{ slug: p.slug! }}
                className="group rounded-2xl border border-border bg-background p-6 transition hover:border-accent hover:shadow-sm"
              >
                <div className="flex items-center justify-between">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent/15 ring-1 ring-accent/30">
                    <Icon name={iconName} color="co" className="h-7 w-7" />
                  </span>
                  <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-accent">
                    {p.lane === "GBP" ? "Map-pack" : "Direct"}
                  </span>
                </div>
                <h3 className="mt-5 font-serif text-xl font-semibold leading-snug">
                  {p.pillar} in Dallas
                </h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Primary keyword: {p.primaryKeyword}
                </p>
                <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-accent">
                  See program <Icon name="chevronright" color="bl" className="h-4 w-4 transition group-hover:translate-x-0.5"  />
                </div>
              </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Industry pillars */}
      <section className="border-b border-border bg-background">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
              Industry-specific
            </p>
            <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight md:text-4xl">
              Funding built around your industry
            </h2>
            <p className="mt-4 text-muted-foreground">
              {dallasIndustryPillars.length} vertical programs with lenders who
              already understand the operating profile, working capital cycle,
              and licensing of your sector in Dallas.
            </p>
          </div>

          <div className="mt-10 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {dallasIndustryPillars.map((p) => {
              const iconName = PRODUCT_ICON_NAME[p.slug!] ?? "briefcase";
              return (
              <Link
                key={p.slug!}
                to="/dallas/$slug"
                params={{ slug: p.slug! }}
                className="group flex items-center gap-3 rounded-xl border border-border bg-card p-4 transition hover:border-accent"
              >
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-accent/15 ring-1 ring-accent/30">
                  <Icon name={iconName} color="co" className="h-6 w-6" />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-serif text-base font-semibold leading-tight">
                    {p.pillar}
                  </p>
                  <p className="mt-0.5 truncate text-xs text-muted-foreground">
                    {p.primaryKeyword}
                  </p>
                </div>
                <Icon name="chevronright" color="bl" className="h-4 w-4 text-muted-foreground transition group-hover:translate-x-0.5 group-hover:text-accent"  />
              </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Service areas */}
      <section className="border-b border-border bg-secondary/40">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-accent">
              Service areas
            </p>
            <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight md:text-4xl">
              Dallas-Fort Worth suburbs we serve
            </h2>
          </div>
          <ul className="mt-8 grid gap-2 text-sm sm:grid-cols-3 lg:grid-cols-5">
            {serviceAreas.map((a) => (
              <li key={a.slug}>
                <a
                  href={`/service-areas/${a.slug}`}
                  className="flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 transition hover:border-accent"
                >
                  <Icon name="checkcircle2" color="bl" className="h-3.5 w-3.5 text-accent"  />
                  {a.city}
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </>
  );
}
